import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/reporting/analytics';
import { motion, useReducedMotion } from '@/lib/motion';
import { UniverseField } from '@/marketing/UniverseField';
import { GlassCube } from '@/marketing/GlassCube';

interface HeroProps {
  onJoinWaitlist: (location: string) => void;
}

// Gated investor briefing (live performance + methodology), on the app domain.
const INVESTOR_URL = 'https://app.novaquantis.ai/investor';

const HEADLINE = { before: 'Hedge-fund intelligence, in a ', accent: 'single ticker', after: '.' };
const SUBHEAD =
  'NovaQuantis is launching an ETF that puts an institutional-grade AI investment engine into one fund anyone can own — the discipline of a quant desk, the simplicity of buying a stock.';
const CHIPS = ['One ticker', 'AI-managed daily', 'Fully transparent'];

export function Hero({ onJoinWaitlist }: HeroProps) {
  const reduced = useReducedMotion();

  const rise = (delay: number) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] as const },
        };

  return (
    <section id="top" className="relative isolate overflow-hidden border-t border-white/8">
      {/* Ambient machine backdrop */}
      <div className="mkt-grid absolute inset-0 -z-20" aria-hidden />
      <div className="mkt-hero-glow absolute inset-0 -z-20" aria-hidden />
      <UniverseField className="absolute inset-0 -z-10 h-full w-full opacity-70" />

      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 pb-24 pt-10 sm:pb-32 sm:pt-14 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.p
            className="mkt-mono mb-6 text-[11px] uppercase tracking-[0.28em] text-[color:var(--nq-blue-bright)]"
            {...rise(0)}
          >
            Democratize Wealth Management
          </motion.p>

          <motion.h1
            className="max-w-4xl font-display text-[clamp(2.6rem,7vw,5.4rem)] font-bold leading-[0.98] tracking-[-0.03em] text-foreground"
            {...rise(0.06)}
          >
            {HEADLINE.before}
            <span className="bg-[image:linear-gradient(100deg,var(--nq-blue-bright)_0%,var(--nq-gold)_100%)] bg-clip-text text-transparent">
              {HEADLINE.accent}
            </span>
            {HEADLINE.after}
          </motion.h1>

          <motion.p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground" {...rise(0.14)}>
            {SUBHEAD}
          </motion.p>

          <motion.div className="mt-5 flex flex-wrap gap-x-4 gap-y-2" {...rise(0.18)}>
            {CHIPS.map((chip) => (
              <span
                key={chip}
                className="mkt-mono text-xs uppercase tracking-[0.16em] text-muted-foreground"
              >
                {chip}
              </span>
            ))}
          </motion.div>

          <motion.div className="mt-8 flex flex-wrap items-center gap-3" {...rise(0.24)}>
            <Button size="lg" onClick={() => onJoinWaitlist('hero')}>
              Join the waitlist
            </Button>
            <Button asChild variant="outline" size="lg">
              <a
                href="#engine"
                onClick={() => trackEvent('cta_click', { cta: 'see_engine', location: 'hero' })}
              >
                See the machine
              </a>
            </Button>
          </motion.div>

          <motion.div className="mt-6" {...rise(0.3)}>
            <a
              href={INVESTOR_URL}
              onClick={() => trackEvent('cta_click', { cta: 'investor_access', location: 'hero' })}
              className="group inline-flex items-center gap-3 rounded-xl border border-white/12 bg-white/[0.02] px-4 py-3 transition-colors hover:border-[color:var(--nq-blue-bright)]/60"
            >
              <Lock className="size-3.5 shrink-0 text-[color:var(--nq-blue-bright)]" aria-hidden />
              <span className="mkt-mono text-xs uppercase tracking-[0.14em] text-muted-foreground transition-colors group-hover:text-foreground">
                Already an investor?{' '}
                <span className="text-foreground/85">Access the briefing</span>
              </span>
              <span
                aria-hidden
                className="text-[color:var(--nq-blue-bright)] transition-transform group-hover:translate-x-0.5"
              >
                →
              </span>
            </a>
          </motion.div>

          <motion.p className="mt-8 max-w-lg text-xs text-white/35" {...rise(0.36)}>
            Concept design — for information only. The NovaQuantis ETF, its fees, and
            regulatory status will be defined in official disclosures and filings.
            Not investment advice.
          </motion.p>
        </div>

        <motion.div
          className="order-first lg:order-none"
          initial={reduced ? false : { opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <GlassCube />
        </motion.div>
      </div>
    </section>
  );
}
